/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { from, Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { Action as MapAction, ActionCreators as MapActionCreators } from '../maps/actions';
import { ActionCreators as GlobalActionCreators } from '../actions';
import { pollingTimer, RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import csvtojson from 'csvtojson';
import { GpsSaMapLayer } from '../../shared/entities/gpsSaMapLayer';
import { MapContextType, OverlaysState } from '../maps/reducer';

// Getting aggregated GPS SA map layers
const requestGpsSaMapLayersSuccess = (status: number, eTag: string, response: GpsSaMapLayer[]): any => {
  return ActionCreators.ReceivedGpsSaMapLayersSuccess.create({ status, eTag, response });
};

const requestGpsSaMapLayersError = (error: AjaxError | { message: string }): any => {
  return ActionCreators.ReceivedGpsSaMapLayersError.create(error);
};

export const requestGpsSaMapLayersEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestGpsSaMapLayers.type),
    pollingTimer(
      action$,
      state$.value.config.appConfig.gpsSaPollingIntervalSeconds,
      ActionCreators.CancelRequestGpsSaMapLayersPoll.type,
    ),
    switchMap((_$action: any): Observable<Action> => {
      // Create headers object dynamically
      const additionalHeaders: any = {};
      if (state$.value.gpsSa.mapLayersETag) {
        additionalHeaders['If-None-Match'] = state$.value.gpsSa.mapLayersETag;
      }

      // Handles ETag in request header
      return ajaxRaw.getCsvWithHeaders(`/gps-sa/map-layer`, additionalHeaders).pipe(
        switchMap((response: AjaxResponse<string>): Observable<Action> => {
          // Parse the CSV into an array of objects
          // See https://github.com/Keyang/node-csvtojson for more details
          return from(
            csvtojson({
              // Convert the CSV columns to the expected types
              colParser: {
                h3Cell: 'string',
                nacPMode: 'number',
                aircraftCount: 'number',
                generatedAt: function (item, _head, _resultRow, _row, _colIdx) {
                  // Convert value to Date object
                  return new Date(item);
                },
              },
              checkType: true,
            })
              .on('error', _error => {
                return requestGpsSaMapLayersError({ message: 'Error parsing CSV to JSON' });
              })
              .fromString(response.response)
              .then(gpsSaMapLayersJson => {
                return requestGpsSaMapLayersSuccess(
                  response.status,
                  response.xhr.getResponseHeader('ETag') || '',
                  gpsSaMapLayersJson,
                );
              }),
          );
        }),
        catchError(
          (error: AjaxError): Observable<Action> =>
            handleErrorInterceptor(error, of(requestGpsSaMapLayersError(error))),
        ),
      );
    }),
  );

const cancelRequestGpsSaMapLayersPollIfNeeded = (state: RootState, checked: boolean): any => {
  if (!checked) {
    // Go through state and see if any instance of gpsSaMapLayersChecked is true
    // ie. if any GPS SA toggle switches are enabled
    const areAnyToggleSwitchesEnabled = Object.values(state.maps.mapContext).some((mapContext: MapContextType) =>
      Object.values(mapContext?.overlaysUiState).some(
        (overlayState: OverlaysState) => overlayState?.gpsSaMapLayersChecked,
      ),
    );

    // If no toggle switches are enabled, cancel the polling
    if (!areAnyToggleSwitchesEnabled) {
      return ActionCreators.CancelRequestGpsSaMapLayersPoll.create();
    }
  }

  // There are still some toggles enabled, so don't cancel the polling
  return GlobalActionCreators.NoOp.create();
};

// Keep polling for GPS SA map layers if any toggle switches are enabled throughout the app
export const stopPollingWhenNoGpsSaMapLayerControlWithinAppIsEnabledEpic = (
  action$: Observable<MapAction>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(MapActionCreators.SetGpsSaMapLayersChecked.type),
    switchMap((action: any) => of(cancelRequestGpsSaMapLayersPollIfNeeded(state$.value, action.payload.checked))),
  );